import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { QueryResult } from '@apollo/client/react/types/types';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { useAuth0 } from '@auth0/auth0-react';
import cuid from 'cuid';
import { useQuery } from '@apollo/client';
import Main from 'routes/Main';
import Menu from 'routes/Menu';
import SingleMenu from 'routes/SingleMenu';
import SingleVenue from 'routes/SingleVenue';
import Boost from 'routes/Boost';
import NoMenus from 'routes/NoMenus';
import SingleItem from 'routes/SingleItem';
import Page404 from 'routes/404';
import SnackbarOutlet from 'components/Snackbar/SnackbarOutlet';
import Modal from 'components/Modal/Modal';
import { UserDataQuery } from 'queries/User';
import { SupportedAppsQuery, VenueDataQuery } from 'queries/Venue';
import { Menu as MenuType, User, type Venue } from 'generated/graphql';
import { useModalContext } from 'common/Context/ModalContext';
import { useErrorModal } from 'utils';
import { AppContext } from 'AppContext';
import type { AllMenuHours, QueuedProductImageUpdate } from 'types';
import BusyModePanel from 'routes/BusyMode';
import OperatingHoursPanel from 'routes/OperatingHours';
import { initAmplitude } from 'utils/Amplitude';
import SurchargePanel from 'routes/Surcharge';
import auth0 from 'auth0-js';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import useConnectRedirection from 'utils/useConnectRedirection';
import { VenueWithMenus } from './components/SingleItem/Details';
import css from './App.module.scss';
import 'scss/Globals.scss';

const env = getEnvironmentVariables();

type VenueDataQueryResult = { menus: MenuType[] };
const useVenueDataQuery = (isAuthenticated: boolean, currentVenue?: Venue): QueryResult<VenueDataQueryResult> =>
  useQuery<VenueDataQueryResult, { locationId?: string }>(VenueDataQuery, {
    variables: {
      locationId: currentVenue?.hashedId
    },
    fetchPolicy: 'cache-and-network',
    skip: !currentVenue || !isAuthenticated
  });

type UserDataQueryResult = { user: User; dietaryEnums: string[] };
const useUserDataQuery = (isAuthenticated: boolean, venueId?: string): QueryResult<UserDataQueryResult> =>
  useQuery<UserDataQueryResult>(UserDataQuery, {
    variables: {
      locationId: venueId
    },
    fetchPolicy: 'cache-and-network',
    skip: !isAuthenticated
  });

const App: React.FC = () => {
  const location = useLocation();
  const sanitisedPathname = sanitizeUrl(location.pathname);
  const venueId = sanitisedPathname.split('/')[2];

  const [allMMHours, setAllMMHours] = useState<AllMenuHours | null>(null);
  const [menuSurchargePublishCount, setMenuSurchargePublishCount] = useState<number>(0);

  const incrementMenuSurchargePublishCount = (): void => {
    setMenuSurchargePublishCount((prev) => prev + 1);
  };

  const transitionContainerRef = useRef<HTMLDivElement>(null);

  const { activeModal } = useModalContext();

  const { isAuthenticated, isLoading, loginWithRedirect, user, logout, getAccessTokenSilently } = useAuth0();

  const webAuth = new auth0.WebAuth({
    domain: env.AUTH0_DOMAIN,
    clientID: env.AUTH0_CLIENT_ID
  });

  useEffect(() => {
    if (user?.sub) {
      initAmplitude(user.sub.replace('auth0|', ''));
    }
  }, [user?.sub]);

  const errorModalHandler = useErrorModal();

  const { data: userData, error } = useUserDataQuery(isAuthenticated, venueId);

  useConnectRedirection();

  const { data: supportedAppsData } = useQuery(SupportedAppsQuery, { skip: !isAuthenticated });
  const supportedApps = supportedAppsData ? supportedAppsData.supportedApps : [];

  const currentUser = userData?.user;
  const dietaryEnums = userData ? userData.dietaryEnums : [];

  const currentVenue = useMemo(() => {
    if (venueId && currentUser?.venues) {
      return currentUser.venues.find((venue: Venue) => venue.hashedId === venueId);
    }

    return undefined;
  }, [venueId, currentUser]);

  const { data: menuData, error: menuError } = useVenueDataQuery(isAuthenticated, currentVenue);

  const [draftMenus, setDraftMenus] = useState<MenuType[]>([]);
  const [queuedProductImageUpdates, updateQueuedProductImageUpdates] = useState<QueuedProductImageUpdate[]>([]);
  const [modifiedMenuIds, setModifiedMenuIds] = useState<string[]>([]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      const stateVerify = cuid();
      localStorage.setItem('auth0StateVerify', stateVerify);
      loginWithRedirect({
        appState: { returnTo: window.location.pathname, stateVerify },
        fragment: 'mm-auth-redirect'
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  // Reset allMMHours cache data when the venueId changes
  useEffect(() => {
    setAllMMHours(null);
  }, [venueId]);

  const checkSessionPromise = async (): Promise<unknown> =>
    new Promise((resolve, reject) => {
      webAuth.checkSession(
        {
          responseType: 'token id_token',
          redirectUri: window.location.origin
        },
        (err, authResult) => {
          if (authResult) {
            resolve(authResult);
          } else {
            reject(err);
          }
        }
      );
    });

  // Refresh nextAuth session on user activity
  useEffect(() => {
    const NOT_IDLE_EVENTS = ['mousemove', 'mousedown', 'touchstart', 'touchmove', 'scroll', 'keydown'];
    // Do not check session more than once every 60 seconds
    const throttlePeriod = 60000;
    let throttleTimeout: ReturnType<typeof setTimeout>;
    let isThrottled = false;

    // Check the session silently and log the user out if there is an error
    const sessionCheck = async (): Promise<void> => {
      try {
        await Promise.all([checkSessionPromise(), getAccessTokenSilently()]);
      } catch (e) {
        logout({
          logoutParams: {
            federated: true
          }
        });
      }
    };

    const throttledSessionCheck = (): void => {
      if (isThrottled) return;
      isThrottled = true;
      throttleTimeout = setTimeout(() => {
        sessionCheck();
        isThrottled = false;
      }, throttlePeriod);
    };
    const addEventListeners = (): void => {
      NOT_IDLE_EVENTS.forEach((event) => {
        document.addEventListener(event, throttledSessionCheck);
      });
    };
    const removeEventListeners = (): void => {
      NOT_IDLE_EVENTS.forEach((event) => {
        document.removeEventListener(event, throttledSessionCheck);
      });
      clearTimeout(throttleTimeout);
    };
    // Check ServerSide Session Object to verify client session
    if (user) addEventListeners();

    return () => {
      removeEventListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const _error = error || menuError;

    if (_error && activeModal?.data?.type !== 'ERROR') {
      errorModalHandler(_error, currentVenue);
    }
  }, [error, menuError, currentVenue, errorModalHandler, activeModal]);

  const currentVenueWithMenus: VenueWithMenus | undefined = useMemo(() => {
    if (currentVenue && menuData) {
      return { ...currentVenue, menus: menuData.menus };
    }

    return undefined;
  }, [currentVenue, menuData]);

  if (!isAuthenticated) {
    return null;
  }

  const showLoader = !!(isLoading || !currentUser || !currentVenueWithMenus);
  const showNoMenu = currentVenueWithMenus?.menus && !currentVenueWithMenus.menus.length && !isLoading;

  return (
    <div data-testid="app-container" className={css.App}>
      <AppContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          queuedProductImageUpdates,
          updateQueuedProductImageUpdates,
          draftMenus,
          setDraftMenus,
          modifiedMenuIds,
          setModifiedMenuIds,
          setAllMMHours,
          allMMHours // allMMHours is all saved hours in menu manager format
        }}
      >
        <Routes location={location}>
          {['/venue/:venueId/boost'].map((path) => {
            return (
              <Route
                key={path}
                path={path}
                element={
                  <Boost currentVenue={currentVenueWithMenus} menuSurchargePublishCount={menuSurchargePublishCount} />
                }
              />
            );
          })}
          <Route
            path="/"
            key={location.key}
            element={
              <Main
                currentUser={currentUser}
                currentVenue={currentVenueWithMenus}
                transitionContainerRef={transitionContainerRef}
                isLoading={showLoader}
                venueId={venueId}
              />
            }
          >
            {!showNoMenu ? (
              ['/', '/venue/:venueId/*'].map((path) => {
                return (
                  <Route
                    path={path}
                    key={path}
                    element={
                      <SingleVenue
                        currentVenue={currentVenueWithMenus}
                        allVenues={currentUser?.venues ?? undefined}
                        isLoading={showLoader}
                      />
                    }
                  >
                    <Route path="about" element={<NoMenus supportedApps={supportedApps} venueId={venueId} isAbout />} />
                    <Route path="menus/:menuId/*" element={<SingleMenu />} />
                    {['*', ''].map((path2) => {
                      return <Route path={path2} key={path2} index={!path} element={<Menu />} />;
                    })}
                  </Route>
                );
              })
            ) : (
              <Route path="*" element={<NoMenus supportedApps={supportedApps} venueId={venueId} />} />
            )}
            <Route path="*" element={<Page404 />} />
          </Route>
        </Routes>
        <TransitionGroup component={null}>
          <CSSTransition key={location.key} classNames="fade" timeout={300}>
            <Routes location={location}>
              {['/venue/:venueId/items/:itemId', '/venue/:venueId/menus/:menuId/items/:itemId'].map((path) => {
                return (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <SingleItem
                        currentUser={currentUser}
                        currentVenue={currentVenueWithMenus}
                        dietaryEnums={dietaryEnums}
                        queuedProductImageUpdates={queuedProductImageUpdates}
                        updateQueuedProductImageUpdates={updateQueuedProductImageUpdates}
                      />
                    }
                  />
                );
              })}
              <Route path="*" element={null} />
            </Routes>
          </CSSTransition>
        </TransitionGroup>
        <TransitionGroup component={null}>
          <CSSTransition key={location.key} classNames="fade" timeout={300}>
            <Routes location={location}>
              {['/venue/:venueId/busy'].map((path) => {
                return (
                  <Route key={path} path={path} element={<BusyModePanel currentVenue={currentVenueWithMenus} />} />
                );
              })}
              <Route path="*" element={null} />
            </Routes>
          </CSSTransition>
        </TransitionGroup>
        <TransitionGroup component={null}>
          <CSSTransition key={location.key} classNames="fade" timeout={300}>
            <Routes location={location}>
              {['/venue/:venueId/pricing'].map((path) => {
                return (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <SurchargePanel
                        currentVenue={currentVenueWithMenus}
                        incrementMenuSurchargePublishCount={incrementMenuSurchargePublishCount}
                      />
                    }
                  />
                );
              })}
              <Route path="*" element={null} />
            </Routes>
          </CSSTransition>
        </TransitionGroup>
        <TransitionGroup component={null}>
          <CSSTransition key={location.key} classNames="fade" timeout={300}>
            <Routes location={location}>
              {[
                '/venue/:venueId/hours',
                '/venue/:venueId/menus/:menuId/hours/:doshiiMenuEncoded',
                '/venue/:venueId/menus/:menuId/hours'
              ].map((path) => {
                return (
                  <Route
                    key={path}
                    path={path}
                    element={<OperatingHoursPanel currentVenue={currentVenueWithMenus} />}
                  />
                );
              })}
              <Route path="*" element={null} />
            </Routes>
          </CSSTransition>
        </TransitionGroup>
        <Modal activeModal={activeModal} />
        <SnackbarOutlet />
      </AppContext.Provider>
    </div>
  );
};

export default App;
